// src/contexts/SiteFilterContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import LocationService from '../services/locationService';
import { useAuth } from './AuthContext';

const SiteFilterContext = createContext({});

const STORAGE_KEY = 'caro_selected_site';

export const useSiteFilter = () => {
  const context = useContext(SiteFilterContext);
  if (!context) {
    throw new Error('useSiteFilter must be used within SiteFilterProvider');
  }
  return context;
};

export const SiteFilterProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [siteLocationIds, setSiteLocationIds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canViewAllSites, setCanViewAllSites] = useState(false);

  // Load sites when user authenticates
  useEffect(() => {
    const loadSites = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('[SiteFilterContext] Loading user sites...');
        const response = await LocationService.getUserSites();

        const sitesData = response?.data?.sites || [];
        const meta = response?.data?.meta || {};

        console.log('[SiteFilterContext] Loaded sites:', sitesData.length, 'isAdmin:', meta.isAdmin);

        setSites(sitesData);
        setIsAdmin(meta.isAdmin || false);
        setCanViewAllSites(meta.canViewAllSites || false);

        // Restore saved selection from localStorage
        const savedSiteId = localStorage.getItem(STORAGE_KEY);

        if (savedSiteId && savedSiteId !== 'all') {
          // Verify the saved site is still accessible
          const savedSiteExists = sitesData.some(
            site => String(site.id) === savedSiteId || String(site._id) === savedSiteId
          );

          if (savedSiteExists) {
            setSelectedSiteId(savedSiteId);
          } else if (meta.canViewAllSites) {
            // Site no longer accessible, default to 'all' if admin
            setSelectedSiteId('all');
          } else if (sitesData.length > 0) {
            // Non-admin: default to first site
            const firstSiteId = String(sitesData[0].id || sitesData[0]._id);
            setSelectedSiteId(firstSiteId);
            localStorage.setItem(STORAGE_KEY, firstSiteId);
          }
        } else if (savedSiteId === 'all' && meta.canViewAllSites) {
          // Admin with 'all' selected
          setSelectedSiteId('all');
        } else if (meta.canViewAllSites) {
          // Admin with no saved selection - default to 'all'
          setSelectedSiteId('all');
          localStorage.setItem(STORAGE_KEY, 'all');
        } else if (sitesData.length > 0) {
          // Non-admin with no saved selection - default to first site
          const firstSiteId = String(sitesData[0].id || sitesData[0]._id);
          setSelectedSiteId(firstSiteId);
          localStorage.setItem(STORAGE_KEY, firstSiteId);
        }

        setLoading(false);
      } catch (err) {
        console.error('[SiteFilterContext] Error loading sites:', err);
        setError(err.message || 'Failed to load sites');
        setLoading(false);
      }
    };

    loadSites();
  }, [isAuthenticated, user]);

  // Load location IDs for selected site
  useEffect(() => {
    // Reset immediately when site changes to prevent filtering with stale IDs
    setSiteLocationIds(null);

    const loadSiteLocationIds = async () => {
      if (!selectedSiteId || selectedSiteId === 'all') {
        // siteLocationIds already set to null above
        return;
      }

      try {
        console.log('[SiteFilterContext] Loading location IDs for site:', selectedSiteId);
        const response = await LocationService.getSiteLocationIds(selectedSiteId);
        const locationIds = response?.data?.locationIds || [];
        console.log('[SiteFilterContext] Loaded location IDs:', locationIds.length, 'for site:', selectedSiteId);
        setSiteLocationIds(locationIds);
      } catch (err) {
        console.error('[SiteFilterContext] Error loading site location IDs:', err);
        setSiteLocationIds([]);  // Empty array instead of null to trigger filter with no results
      }
    };

    loadSiteLocationIds();
  }, [selectedSiteId]);

  // Change selected site
  const selectSite = useCallback((siteId) => {
    console.log('[SiteFilterContext] Selecting site:', siteId);

    // Validate selection
    if (siteId === 'all' && !canViewAllSites) {
      console.warn('[SiteFilterContext] User cannot view all sites');
      return;
    }

    if (siteId !== 'all') {
      const siteExists = sites.some(
        site => String(site.id) === String(siteId) || String(site._id) === String(siteId)
      );
      if (!siteExists) {
        console.warn('[SiteFilterContext] Site not found:', siteId);
        return;
      }
    }

    setSelectedSiteId(siteId);
    localStorage.setItem(STORAGE_KEY, siteId);
  }, [sites, canViewAllSites]);

  // Get currently selected site object
  const getSelectedSite = useCallback(() => {
    if (!selectedSiteId || selectedSiteId === 'all') {
      return null;
    }
    return sites.find(
      site => String(site.id) === String(selectedSiteId) || String(site._id) === String(selectedSiteId)
    ) || null;
  }, [selectedSiteId, sites]);

  // Get filter parameters for API calls
  // Returns object with locationIds or empty object if 'all' selected
  // For non-admin users with no site access, returns empty locationIds array to filter to zero results
  const getSiteFilterParams = useCallback(() => {
    // Non-admin user with no sites assigned - enforce empty filter
    const noAccess = !canViewAllSites && !selectedSiteId && sites.length === 0;
    if (noAccess) {
      return { locationIds: [] };
    }
    if (!selectedSiteId || selectedSiteId === 'all' || !siteLocationIds) {
      return {};
    }
    return {
      locationIds: siteLocationIds
    };
  }, [selectedSiteId, siteLocationIds, canViewAllSites, sites.length]);

  // Check if a specific location ID is within the selected site
  const isLocationInSelectedSite = useCallback((locationId) => {
    // Non-admin user with no sites assigned - no location is accessible
    const noAccess = !canViewAllSites && !selectedSiteId && sites.length === 0;
    if (noAccess) {
      return false;
    }
    if (!selectedSiteId || selectedSiteId === 'all') {
      return true; // All locations visible when 'all' selected
    }
    if (!siteLocationIds) {
      return true; // No filter if location IDs not loaded yet
    }
    return siteLocationIds.includes(Number(locationId)) || siteLocationIds.includes(String(locationId));
  }, [selectedSiteId, siteLocationIds, canViewAllSites, sites.length]);

  // Refresh sites list
  const refreshSites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await LocationService.getUserSites();
      const sitesData = response?.data?.sites || [];
      const meta = response?.data?.meta || {};
      setSites(sitesData);
      setIsAdmin(meta.isAdmin || false);
      setCanViewAllSites(meta.canViewAllSites || false);
      setLoading(false);
    } catch (err) {
      console.error('[SiteFilterContext] Error refreshing sites:', err);
      setLoading(false);
    }
  }, []);

  // For non-admin users with no sites assigned, they should see nothing
  // This means we need to enforce filtering even if no site is selected
  const shouldEnforceFilter = !canViewAllSites && !selectedSiteId && sites.length === 0;

  // Compute effective siteLocationIds - empty array when user has no site access
  // This ensures filtering will show zero results for non-admin users without site access
  const effectiveSiteLocationIds = shouldEnforceFilter ? [] : siteLocationIds;

  const value = {
    // State
    sites,
    selectedSiteId,
    siteLocationIds: effectiveSiteLocationIds,
    loading,
    error,
    isAdmin,
    canViewAllSites,

    // Actions
    selectSite,
    refreshSites,

    // Helpers
    getSelectedSite,
    getSiteFilterParams,
    isLocationInSelectedSite,

    // Computed
    isAllSitesSelected: selectedSiteId === 'all',
    // hasSiteFilter is true when:
    // 1. A specific site is selected (not 'all')
    // 2. OR user is non-admin with no sites (should see nothing)
    hasSiteFilter: (selectedSiteId && selectedSiteId !== 'all') || shouldEnforceFilter,
    // Non-admin users with no assigned sites should have empty locationIds filter
    noAccessToAnySite: shouldEnforceFilter,
    selectedSiteName: selectedSiteId === 'all'
      ? 'All Sites'
      : selectedSiteId
        ? (getSelectedSite()?.friendlyName || getSelectedSite()?.name || 'Unknown Site')
        : 'No Site Selected'
  };

  return (
    <SiteFilterContext.Provider value={value}>
      {children}
    </SiteFilterContext.Provider>
  );
};

export default SiteFilterContext;
